import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { local } from 'embroider-css-modules';
import { IncrementFor } from 'frontend-utils';
import type { Hotel } from 'frontend-utils/models';
import type { BenchmarkInsightCompetitorsCompetitor } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

import styles from './competitors-list-item.module.css';

interface HotelBenchmarkInsightCompetitorsCompetitorsListItemSignature {
  Args: {
    competitor: BenchmarkInsightCompetitorsCompetitor;
  };
}

export default class HotelBenchmarkInsightCompetitorsCompetitorsListItem extends Component<HotelBenchmarkInsightCompetitorsCompetitorsListItemSignature> {
  @service('benchmark-insight/beni-competitors-service')
  declare benchmarkInsightBeniCompetitorsService: Services['benchmark-insight/beni-competitors-service'];

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  redirectToSource = (
    competitor: BenchmarkInsightCompetitorsCompetitor,
  ): void => {
    this.benchmarkInsightBeniCompetitorsService.redirectToSource(competitor);
  };

  get hotel(): Hotel {
    return this.hotelAndMarketService.hotel;
  }

  get isHovered(): boolean {
    const hoveredId =
      this.benchmarkInsightBeniCompetitorsService.hoveredCompetitor?.id;
    return hoveredId === this.args.competitor.id;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get stars() {
    return this.args.competitor.stars;
  }

  @action
  onHover(competitor: BenchmarkInsightCompetitorsCompetitor): void {
    this.benchmarkInsightBeniCompetitorsService.handleHover(competitor);
  }

  @action
  onHoverOut(): void {
    this.benchmarkInsightBeniCompetitorsService.handleHover();
  }

  <template>
    <li class={{styles.list-item}}>
      <div class={{styles.list-item-inner}}>
        <div
          {{on "mouseenter" (fn this.onHover @competitor)}}
          {{on "mouseleave" this.onHoverOut}}
          class="truncate"
        >
          <div
            class={{concat
              (local styles "hotel-name" (if this.isHovered "hovered"))
              " "
              "truncate"
            }}
            role="button"
            title={{@competitor.competitorName}}
            {{on "click" (fn this.redirectToSource @competitor)}}
          >
            {{@competitor.competitorName}}
          </div>
        </div>
        <div class={{styles.hotel-rating}}>
          <IncrementFor @times={{this.stars}}>
            <PrismIcon @name="star-filled" @size="extra-small" />
          </IncrementFor>
        </div>
        <div>
          {{@competitor.distance}}
        </div>
      </div>
    </li>
  </template>
}
